import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import config from '../config';
import { fetchEventStatus, toggleAttended, toggleCaught, resolveEventStatus, getIdToken } from '../attendedUtils';
import { UploadModal } from './MyImages';

// ── Info tab field list ───────────────────────────────────────────────────────
const INFO_TABS = ['Info', 'Capacity', 'Photos'];

// ── Photos sub-component (mirrors ArtistDetail PhotosTab) ────────────────────
function PhotosTab({ eventId, eventName, defaultImageId, onDefaultChanged }) {
    const [images, setImages] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [settingDefault, setSettingDefault] = useState(null);
    const [removingDefault, setRemovingDefault] = useState(false);
    const [lightbox, setLightbox] = useState(null);
    const [showUpload, setShowUpload] = useState(false);

    const loadPhotos = useCallback(async () => {
        if (!eventId) return;
        setLoading(true);
        try {
            const token = await getIdToken();
            const res = await axios.get(`${config.API_URL}/images`, {
                params: { entityType: 'event', entityId: eventId },
                headers: { Authorization: token },
            });
            const imgs = res.data.images || [];
            setImages(imgs);
            setError('');
            if (defaultImageId) {
                const defaultImg = imgs.find(i => i.id === defaultImageId);
                if (defaultImg) onDefaultChanged(defaultImg.id, defaultImg.image_url);
            }
        } catch {
            setError('Failed to load photos.');
        } finally {
            setLoading(false);
        }
    }, [eventId, defaultImageId, onDefaultChanged]);

    useEffect(() => { loadPhotos(); }, [loadPhotos]);

    const handleUploaded = (img) => {
        setImages(prev => [img, ...prev]);
        setShowUpload(false);
    };

    const handleRemoveDefault = async () => {
        setRemovingDefault(true);
        try {
            const token = await getIdToken();
            await axios.post(
                `${config.API_URL}/images/set-default`,
                { imageId: null, entityType: 'event', entityId: eventId },
                { headers: { Authorization: token } }
            );
            onDefaultChanged(null, null);
        } catch (e) {
            alert(e.response?.data?.error || 'Failed to remove default image.');
        } finally {
            setRemovingDefault(false);
        }
    };

    const handleSetDefault = async (image) => {
        setSettingDefault(image.id);
        try {
            const token = await getIdToken();
            await axios.post(
                `${config.API_URL}/images/set-default`,
                { imageId: image.id, entityType: 'event', entityId: eventId },
                { headers: { Authorization: token } }
            );
            onDefaultChanged(image.id, image.image_url);
        } catch (e) {
            alert(e.response?.data?.error || 'Failed to set default image.');
        } finally {
            setSettingDefault(null);
        }
    };

    if (loading) return <div style={styles.emptyTab}>Loading photos…</div>;
    if (error) return <div style={styles.emptyTab}>{error}</div>;

    return (
        <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                <div>
                    {defaultImageId && (
                        <button
                            onClick={handleRemoveDefault}
                            disabled={removingDefault}
                            style={{ background: 'transparent', border: '1px solid #555', borderRadius: '6px', color: '#aaa', padding: '5px 12px', fontSize: '12px', cursor: 'pointer', opacity: removingDefault ? 0.6 : 1 }}
                        >
                            {removingDefault ? 'Removing…' : '✕ Remove Default'}
                        </button>
                    )}
                </div>
                <button onClick={() => setShowUpload(true)} style={styles.addPhotoBtn}>
                    + Add Photo
                </button>
            </div>

            {images.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '20px 0' }}>
                    <div style={{ fontSize: '32px', marginBottom: '10px' }}>📷</div>
                    <div style={{ color: '#666', fontSize: '14px' }}>No photos yet for this event.</div>
                </div>
            ) : (
                <div style={styles.photoGrid}>
                    {images.map(img => {
                        const isDefault = img.id === defaultImageId;
                        return (
                            <div key={img.id} style={{ ...styles.photoCard, ...(isDefault ? styles.photoCardDefault : {}) }}>
                                <div style={styles.photoThumb} onClick={() => setLightbox(img)}>
                                    <img src={img.image_url} alt={img.caption || eventName} style={styles.photoThumbImg} loading="lazy" />
                                    {isDefault && <span style={styles.defaultBadge}>★ Default</span>}
                                    {!img.is_public && <span style={styles.privateBadge}>🔒</span>}
                                </div>
                                <div style={styles.photoCardBody}>
                                    {img.caption && <div style={styles.photoCaption}>{img.caption}</div>}
                                    {!isDefault && img.is_public && (
                                        <button onClick={() => handleSetDefault(img)} disabled={settingDefault === img.id} style={styles.setDefaultBtn}>
                                            {settingDefault === img.id ? 'Setting…' : '★ Set as Default'}
                                        </button>
                                    )}
                                    {isDefault && <div style={styles.isDefaultLabel}>Current default photo</div>}
                                    {!img.is_public && <div style={{ fontSize: '11px', color: '#666', marginTop: '4px' }}>Private — only visible to you</div>}
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {lightbox && (
                <div style={styles.lightboxOverlay} onClick={() => setLightbox(null)}>
                    <div style={{ position: 'relative' }}>
                        <img src={lightbox.image_url} alt={lightbox.caption || ''} style={{ maxWidth: '90vw', maxHeight: '85vh', objectFit: 'contain', borderRadius: '8px' }} />
                        {lightbox.caption && <div style={styles.lightboxCaption}>{lightbox.caption}</div>}
                        <button onClick={() => setLightbox(null)} style={styles.lightboxClose}>✕</button>
                    </div>
                </div>
            )}

            {showUpload && (
                <UploadModal
                    onClose={() => setShowUpload(false)}
                    onUploaded={handleUploaded}
                    presetEntityType="event"
                    presetEntityId={eventId}
                    presetEntityName={eventName}
                />
            )}
        </div>
    );
}

// ── Main component ────────────────────────────────────────────────────────────
function EventDetail({ slugOverride } = {}) {
    const params = useParams();
    const slug = slugOverride || params.slug;
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Attended / caught state
    const [attended, setAttended] = useState(false);
    const [performed, setPerformed] = useState(false);
    const [worked, setWorked] = useState(false);
    const [artistStatus, setArtistStatus] = useState({});
    const [attendedLoading, setAttendedLoading] = useState(false);
    const [caughtLoading, setCaughtLoading] = useState({});

    // Tab state
    const [activeInfoTab, setActiveInfoTab] = useState(0);

    // Image state
    const [defaultImageId, setDefaultImageId] = useState(null);
    const [defaultImageUrl, setDefaultImageUrl] = useState(null);
    const [defaultImageResolving, setDefaultImageResolving] = useState(false);

    // Footnote state
    const [allArtists, setAllArtists] = useState([]);
    const [footnotes, setFootnotes] = useState([]);
    const [editingFootnotes, setEditingFootnotes] = useState(false);
    const [draftFootnotes, setDraftFootnotes] = useState([]);
    const [savingFootnotes, setSavingFootnotes] = useState(false);
    const [footnoteError, setFootnoteError] = useState(null);

    const loadEventStatus = useCallback(async (eventId) => {
        try {
            const status = await fetchEventStatus(eventId);
            setAttended(status.attended);
            setPerformed(status.performed || false);
            setWorked(status.worked || false);
            setArtistStatus(status.artist_status || {});
        } catch (e) {
            // Not logged in — buttons just won't show status
        }
    }, []);

    useEffect(() => {
        async function load() {
            try {
                const [eventRes, artistsRes] = await Promise.all([
                    axios.get(`${config.API_URL}/events/${slug}`),
                    axios.get(`${config.API_URL}/artists`),
                ]);
                setData(eventRes.data);
                setFootnotes(eventRes.data.footnotes || []);
                setAllArtists(artistsRes.data);
                setLoading(false);

                setDefaultImageId(null);
                setDefaultImageUrl(null);
                setDefaultImageResolving(false);

                const ev = eventRes.data.event;
                if (!ev) return;

                const hasImageUrl = !!ev.image_url;
                const hasDefaultId = !!ev.default_image_id;

                if (hasDefaultId || !hasImageUrl) {
                    setDefaultImageResolving(true);
                    try {
                        const token = await getIdToken();
                        const imgRes = await axios.get(`${config.API_URL}/images`, {
                            params: { entityType: 'event', entityId: ev.id },
                            headers: { Authorization: token },
                        });
                        const imgs = imgRes.data.images || [];

                        if (hasDefaultId) {
                            setDefaultImageId(ev.default_image_id);
                            const defaultImg = imgs.find(i => i.id === ev.default_image_id);
                            if (defaultImg) setDefaultImageUrl(defaultImg.image_url);
                        } else if (!hasImageUrl && imgs.length > 0) {
                            const publicImgs = imgs.filter(i => i.is_public);
                            if (publicImgs.length > 0) {
                                const oldest = publicImgs[publicImgs.length - 1];
                                setDefaultImageUrl(oldest.image_url);
                            }
                        }
                    } catch { /* fall back gracefully */ }
                    finally { setDefaultImageResolving(false); }
                }

                if (eventRes.data.event) {
                    loadEventStatus(eventRes.data.event.id);
                }

                const arts = eventRes.data.artists || [];
                const headliner = arts.find(a => a.role === 'headliner');
                if (ev) {
                    const dateStr = ev.event_date
                        ? new Date(ev.event_date).toISOString().slice(0, 10).replace(/-/g, '')
                        : null;
                    const parts = [dateStr, ev.title || null, ev.venue_name, headliner ? headliner.artist_name : null]
                        .filter(Boolean);
                    document.title = `SHARC - ${parts.join(' - ')}`;
                }
            } catch {
                setError('Failed to load event');
                setLoading(false);
            }
        }
        load();
        return () => { document.title = 'SHARC - Shows Archive'; };
    }, [slug, loadEventStatus]);

    // ── Image default callback (used by PhotosTab) ───────────────────────────
    const handleDefaultChanged = useCallback((id, url) => {
        setDefaultImageId(id);
        setDefaultImageUrl(url);
    }, []);

    // ── Attended / caught handlers ────────────────────────────────────────────
    const handleToggleAttended = async () => {
        if (attendedLoading || !data) return;
        setAttendedLoading(true);
        try {
            const result = await toggleAttended(data.event.id);
            setAttended(result.attended);
            setPerformed(result.performed || false);
            setWorked(result.worked || false);
            if (!result.attended) {
                setArtistStatus({});
            } else if (result.artist_status) {
                setArtistStatus(result.artist_status);
            }
        } catch (e) {
            console.error('toggleAttended error:', e);
        } finally {
            setAttendedLoading(false);
        }
    };

    const handleToggleCaught = async (artistId) => {
        if (caughtLoading[artistId] || !data) return;
        setCaughtLoading(prev => ({ ...prev, [artistId]: true }));
        try {
            const result = await toggleCaught(data.event.id, artistId);
            setArtistStatus(prev => ({ ...prev, [artistId]: result.caught }));
        } catch (e) {
            console.error('toggleCaught error:', e);
        } finally {
            setCaughtLoading(prev => ({ ...prev, [artistId]: false }));
        }
    };

    // ── Footnote helpers ──────────────────────────────────────────────────────
    const startEditingFootnotes = () => {
        setDraftFootnotes(footnotes.map(fn => ({
            description: fn.description,
            url: fn.url || '',
            artist_id: fn.artist_id ? String(fn.artist_id) : '',
        })));
        setFootnoteError(null);
        setEditingFootnotes(true);
    };

    const cancelEditingFootnotes = () => {
        setEditingFootnotes(false);
        setFootnoteError(null);
    };

    const addFootnote = () => {
        setDraftFootnotes(prev => [...prev, { description: '', url: '', artist_id: '' }]);
    };

    const updateFootnote = (index, field, value) => {
        setDraftFootnotes(prev => prev.map((fn, i) =>
            i === index ? { ...fn, [field]: value } : fn
        ));
    };

    const removeFootnote = (index) => {
        setDraftFootnotes(prev => prev.filter((_, i) => i !== index));
    };

    const saveFootnotes = async () => {
        if (draftFootnotes.some(fn => !fn.description.trim())) {
            setFootnoteError('Each footnote must have a description.');
            return;
        }
        setSavingFootnotes(true);
        setFootnoteError(null);
        try {
            const payload = {
                footnotes: draftFootnotes
                    .filter(fn => fn.description.trim())
                    .map(fn => ({
                        description: fn.description.trim(),
                        url: fn.url.trim() || null,
                        artist_id: fn.artist_id ? parseInt(fn.artist_id) : null,
                    }))
            };
            const eventId = data && data.event ? data.event.id : slug;
            const res = await axios.put(`${config.API_URL}/events/${eventId}/footnotes`, payload);
            setFootnotes(res.data.footnotes || []);
            setEditingFootnotes(false);
        } catch (err) {
            setFootnoteError('Failed to save footnotes. Please try again.');
        } finally {
            setSavingFootnotes(false);
        }
    };

    // ── Render guards ─────────────────────────────────────────────────────────
    if (loading) return <div style={{ minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#555', fontSize: '14px' }}>Loading event...</div>;
    if (error) return <div style={styles.error}>{error}</div>;
    if (!data) return null;

    const { event, artists, number_of_artists, promoters } = data;

    const formatDate = (dateStr) => {
        if (!dateStr) return '—';
        return new Date(dateStr).toLocaleDateString('en-US', {
            weekday: 'long', year: 'numeric', month: 'long', day: 'numeric', timeZone: 'UTC'
        });
    };

    const headliner = artists.find(a => a.role === 'headliner');
    const supporters = artists.filter(a => a.role !== 'headliner').reverse();
    const heroImage = defaultImageUrl || event.image_url;
    const eventStatus = resolveEventStatus(performed, worked, attended);

    // ── Capacity tab helpers ──────────────────────────────────────────────────
    const estPct = event.est_attendance && event.venue_capacity
        ? Math.round((event.est_attendance_number / event.venue_capacity) * 100)
        : null;

    return (
        <div style={{ width: '100%', minWidth: 0 }}>

            {/* ── Top action bar ── */}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginBottom: '20px', alignItems: 'center' }}>
                {eventStatus === 'performed' ? (
                    <span style={styles.performedBadge}>Performed</span>
                ) : eventStatus === 'worked' ? (
                    <span style={styles.workedBadge}>Worked</span>
                ) : (
                    <button
                        onClick={handleToggleAttended}
                        disabled={attendedLoading}
                        style={attended ? styles.attendedBtnOn : styles.attendedBtnOff}
                    >
                        {attendedLoading ? '…' : attended ? '✓ Attended' : 'Mark Attended'}
                    </button>
                )}
                <Link to={`/events/${event.slug || event.id}/edit`} style={styles.editButton}>
                    Edit Event
                </Link>
            </div>

            {/* ── Section 1: shared header row + content row ── */}

            {/* Header row: tabs left, Lineup label right — same baseline */}
            <div style={styles.panelHeaderRow}>
                <div style={styles.leftPaneHeader}>
                    {INFO_TABS.map((tab, i) => (
                        <button
                            key={tab}
                            onClick={() => setActiveInfoTab(i)}
                            style={{ ...styles.infoTabBtn, ...(activeInfoTab === i ? styles.infoTabBtnActive : {}) }}
                        >
                            {tab}
                        </button>
                    ))}
                </div>
                <div style={styles.rightPaneHeader}>
                    <span style={styles.lineupHeaderText}>Lineup</span>
                </div>
            </div>

            {/* Content row: info panel left, lineup cards right */}
            <div style={styles.splitLayout}>

                {/* ── LEFT 60%: Info tab content ── */}
                <div style={styles.leftPane}>
                    <div style={styles.infoTabContent}>

                        {/* ── Info tab ── */}
                        {activeInfoTab === 0 && (
                            <div style={styles.infoLayout}>
                                {/* Hero image */}
                                <div style={styles.heroImageWrap}>
                                    {!defaultImageResolving && heroImage ? (
                                        <img
                                            src={heroImage}
                                            alt={event.title || (headliner ? headliner.artist_name : 'Event')}
                                            style={styles.heroImage}
                                            onError={e => {
                                                e.target.outerHTML = `<div style="width:200px;height:260px;border-radius:8px;border:1px solid #333;background:#1e1e1e;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:8px;flex-shrink:0"><div style="font-size:36px;opacity:0.3">🎟️</div><div style="font-size:11px;color:#555;text-transform:uppercase;letter-spacing:0.5px">No Image</div></div>`;
                                            }}
                                        />
                                    ) : (
                                        <div style={styles.noImageBox}>
                                            <div style={styles.noImageIcon}>🎟️</div>
                                            <div style={styles.noImageLabel}>{defaultImageResolving ? '…' : 'No Image'}</div>
                                        </div>
                                    )}
                                </div>

                                {/* Info fields */}
                                <table style={styles.infoTable}>
                                    <tbody>
                                        <tr>
                                            <td style={styles.infoLabel}>Date:</td>
                                            <td style={styles.infoValue}>{formatDate(event.event_date)}</td>
                                        </tr>
                                        <tr>
                                            <td style={styles.infoLabel}>Venue:</td>
                                            <td style={styles.infoValue}>
                                                <Link to={`/venues/${event.venue_slug || event.venue_id}`} style={styles.infoLink}>
                                                    {event.venue_name}
                                                </Link>
                                                {' · '}
                                                <span style={{ color: '#aaa' }}>
                                                    {[event.venue_city, event.venue_state, event.venue_country].filter(Boolean).join(', ')}
                                                </span>
                                            </td>
                                        </tr>
                                        {event.festival_id && event.festival_name && (
                                            <tr>
                                                <td style={styles.infoLabel}>Festival:</td>
                                                <td style={styles.infoValue}>
                                                    <Link to={`/festivals/${event.festival_slug}`} style={styles.infoLink}>{event.festival_name}</Link>
                                                </td>
                                            </tr>
                                        )}
                                        <tr>
                                            <td style={styles.infoLabel}>Event Title:</td>
                                            <td style={styles.infoValue}>{event.title || <span style={styles.emptyField}>—</span>}</td>
                                        </tr>
                                        <tr>
                                            <td style={styles.infoLabel}>Stage:</td>
                                            <td style={styles.infoValue}>{event.stage_name || <span style={styles.emptyField}>—</span>}</td>
                                        </tr>
                                        <tr>
                                            <td style={styles.infoLabel}>Promoter:</td>
                                            <td style={styles.infoValue}>
                                                {promoters && promoters.length > 0 ? (
                                                    promoters.map((p, i) => (
                                                        <span key={p.id}>
                                                            <Link to={`/promoters/${p.slug}`} style={styles.infoLink}>{p.name}</Link>
                                                            {i < promoters.length - 1 && ', '}
                                                        </span>
                                                    ))
                                                ) : (
                                                    <span style={styles.emptyField}>—</span>
                                                )}
                                            </td>
                                        </tr>
                                        <tr>
                                            <td style={styles.infoLabel}>Age Restriction:</td>
                                            <td style={styles.infoValue}>{event.age_restriction || <span style={styles.emptyField}>—</span>}</td>
                                        </tr>
                                        <tr>
                                            <td style={styles.infoLabel}># of Artists:</td>
                                            <td style={styles.infoValue}>{number_of_artists}</td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        )}

                        {/* ── Capacity tab ── */}
                        {activeInfoTab === 1 && (
                            <table style={styles.infoTable}>
                                <tbody>
                                    <tr>
                                        <td style={styles.infoLabel}>Venue Capacity:</td>
                                        <td style={styles.infoValue}>
                                            {event.venue_capacity ? event.venue_capacity.toLocaleString() : <span style={styles.emptyField}>—</span>}
                                        </td>
                                    </tr>
                                    <tr>
                                        <td style={styles.infoLabel}>Stage Capacity:</td>
                                        <td style={styles.infoValue}>
                                            {event.stage_capacity ? event.stage_capacity.toLocaleString() : <span style={styles.emptyField}>—</span>}
                                        </td>
                                    </tr>
                                    <tr>
                                        <td style={styles.infoLabel}>Est. Attendance:</td>
                                        <td style={styles.infoValue}>
                                            {event.est_attendance || <span style={styles.emptyField}>—</span>}
                                        </td>
                                    </tr>
                                    <tr>
                                        <td style={styles.infoLabel}>Est. # of Attendees:</td>
                                        <td style={styles.infoValue}>
                                            {event.est_attendance_number > 0
                                                ? `~${event.est_attendance_number.toLocaleString()}`
                                                : <span style={styles.emptyField}>—</span>}
                                        </td>
                                    </tr>
                                    {estPct !== null && (
                                        <tr>
                                            <td style={styles.infoLabel}>% of Capacity:</td>
                                            <td style={styles.infoValue}>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                                    <div style={{ flex: 1, height: '8px', backgroundColor: '#2c2c2c', borderRadius: '4px', overflow: 'hidden', maxWidth: '160px' }}>
                                                        <div style={{ width: `${Math.min(estPct, 100)}%`, height: '100%', backgroundColor: '#bb86fc', borderRadius: '4px' }} />
                                                    </div>
                                                    <span style={{ color: '#bb86fc', fontWeight: 'bold', fontSize: '14px' }}>{estPct}%</span>
                                                </div>
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        )}

                        {/* ── Photos tab ── */}
                        {activeInfoTab === 2 && data?.event && (
                            <PhotosTab
                                eventId={event.id}
                                eventName={event.title || (headliner ? headliner.artist_name : `Event #${event.id}`)}
                                defaultImageId={defaultImageId}
                                onDefaultChanged={handleDefaultChanged}
                            />
                        )}
                    </div>
                </div>

                {/* ── RIGHT 40%: Lineup cards ── */}
                <div style={styles.rightPane}>
                    <div style={styles.lineupList}>
                        {/* Headliner */}
                        {headliner && (
                            <div style={{ ...styles.lineupCard, ...styles.lineupCardHeadliner }}>
                                <div style={styles.lineupPosition}>#1</div>
                                <div style={styles.lineupInfo}>
                                    <Link to={`/artists/${headliner.artist_slug || headliner.artist_id}`} style={styles.artistLink}>
                                        {headliner.artist_name}
                                    </Link>
                                    <div style={styles.artistLocation}>
                                        {[headliner.artist_city, headliner.artist_state, headliner.artist_country].filter(Boolean).join(', ')}
                                    </div>
                                </div>
                                <span style={styles.headlinerBadge}>HEADLINER</span>
                                {attended && !worked && (
                                    <button
                                        onClick={() => handleToggleCaught(headliner.artist_id)}
                                        disabled={!!caughtLoading[headliner.artist_id]}
                                        style={artistStatus[headliner.artist_id] === true
                                            ? styles.caughtBtnOn
                                            : artistStatus[headliner.artist_id] === false
                                            ? styles.caughtBtnMissed
                                            : styles.caughtBtnOff}
                                    >
                                        {caughtLoading[headliner.artist_id] ? '…'
                                            : artistStatus[headliner.artist_id] === true ? '✓ Caught'
                                            : artistStatus[headliner.artist_id] === false ? '✕ Missed'
                                            : 'Caught?'}
                                    </button>
                                )}
                            </div>
                        )}

                        {/* Support acts */}
                        {supporters.map((artist, index) => (
                            <div key={artist.id} style={styles.lineupCard}>
                                <div style={{ ...styles.lineupPosition, color: '#888' }}>
                                    #{index + 2}
                                </div>
                                <div style={styles.lineupInfo}>
                                    <Link to={`/artists/${artist.artist_slug || artist.artist_id}`} style={{ ...styles.artistLink, fontSize: '15px' }}>
                                        {artist.artist_name}
                                    </Link>
                                    <div style={styles.artistLocation}>
                                        {[artist.artist_city, artist.artist_state, artist.artist_country].filter(Boolean).join(', ')}
                                    </div>
                                </div>
                                <span style={styles.supportBadge}>SUPPORT</span>
                                {attended && !worked && (
                                    <button
                                        onClick={() => handleToggleCaught(artist.artist_id)}
                                        disabled={!!caughtLoading[artist.artist_id]}
                                        style={artistStatus[artist.artist_id] === true
                                            ? styles.caughtBtnOn
                                            : artistStatus[artist.artist_id] === false
                                            ? styles.caughtBtnMissed
                                            : styles.caughtBtnOff}
                                    >
                                        {caughtLoading[artist.artist_id] ? '…'
                                            : artistStatus[artist.artist_id] === true ? '✓ Caught'
                                            : artistStatus[artist.artist_id] === false ? '✕ Missed'
                                            : 'Caught?'}
                                    </button>
                                )}
                            </div>
                        ))}

                        {artists.length === 0 && (
                            <div style={{ color: '#666', fontSize: '14px', fontStyle: 'italic', padding: '8px 0' }}>No artists listed.</div>
                        )}
                    </div>
                </div>
            </div>

            {/* ── Bottom section: Footnotes ── */}
            <div style={styles.section}>
                <div style={styles.sectionHeaderRow}>
                    <h2 style={styles.sectionTitle}>Footnotes</h2>
                    {!editingFootnotes && (
                        <button onClick={startEditingFootnotes} style={styles.editSectionButton}>
                            {footnotes.length > 0 ? 'Edit Footnotes' : '+ Add Footnotes'}
                        </button>
                    )}
                </div>

                {/* Read mode */}
                {!editingFootnotes && (
                    footnotes.length === 0 ? (
                        <div style={styles.emptyText}>No footnotes for this event.</div>
                    ) : (
                        <div style={styles.footnoteList}>
                            {footnotes.map((fn, index) => (
                                <div key={fn.id} style={styles.footnoteCard}>
                                    <div style={styles.footnoteNumber}>{index + 1}</div>
                                    <div style={styles.footnoteContent}>
                                        <div style={styles.footnoteDescription}>{fn.description}</div>
                                        <div style={styles.footnoteMeta}>
                                            {fn.artist_name && (
                                                <Link
                                                    to={`/artists/${fn.artist_slug || fn.artist_id}`}
                                                    style={styles.footnoteArtistLink}
                                                >
                                                    {fn.artist_name}
                                                </Link>
                                            )}
                                            {fn.url && (
                                                <a
                                                    href={fn.url}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    style={styles.footnoteUrlLink}
                                                >
                                                    Source ↗
                                                </a>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )
                )}

                {/* Edit mode */}
                {editingFootnotes && (
                    <div style={styles.editBox}>
                        {footnoteError && (
                            <div style={styles.footnoteErrorMsg}>{footnoteError}</div>
                        )}

                        {draftFootnotes.length === 0 && (
                            <div style={styles.emptyText}>No footnotes yet. Click "+ Add Footnote" to add one.</div>
                        )}

                        {draftFootnotes.map((fn, index) => (
                            <div key={index} style={styles.footnoteEditCard}>
                                <div style={styles.footnoteEditHeader}>
                                    <span style={styles.footnoteEditNumber}>#{index + 1}</span>
                                    <button
                                        onClick={() => removeFootnote(index)}
                                        style={styles.removeFootnoteButton}
                                    >✕ Remove</button>
                                </div>

                                <div style={styles.footnoteEditField}>
                                    <label style={styles.footnoteEditLabel}>Description *</label>
                                    <input
                                        type="text"
                                        value={fn.description}
                                        onChange={e => updateFootnote(index, 'description', e.target.value)}
                                        placeholder="e.g. First time these two artists performed together"
                                        style={styles.footnoteInput}
                                    />
                                </div>

                                <div style={styles.footnoteEditRow}>
                                    <div style={{ flex: 1 }}>
                                        <label style={styles.footnoteEditLabel}>URL (optional)</label>
                                        <input
                                            type="text"
                                            value={fn.url}
                                            onChange={e => updateFootnote(index, 'url', e.target.value)}
                                            placeholder="https://..."
                                            style={styles.footnoteInput}
                                        />
                                    </div>
                                    <div style={{ flex: 1 }}>
                                        <label style={styles.footnoteEditLabel}>Referenced Artist (optional)</label>
                                        <select
                                            value={fn.artist_id}
                                            onChange={e => updateFootnote(index, 'artist_id', e.target.value)}
                                            style={styles.footnoteInput}
                                        >
                                            <option value="">No artist</option>
                                            {allArtists.map(a => (
                                                <option key={a.id} value={a.id}>
                                                    {a.name}{a.city ? ` — ${a.city}` : ''}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                </div>
                            </div>
                        ))}

                        <div style={styles.footnoteEditActions}>
                            <button onClick={addFootnote} style={styles.addFootnoteButton}>+ Add Footnote</button>
                            <div style={styles.footnoteEditButtons}>
                                <button onClick={cancelEditingFootnotes} style={styles.cancelButton}>Cancel</button>
                                <button
                                    onClick={saveFootnotes}
                                    disabled={savingFootnotes}
                                    style={styles.saveButton}
                                >
                                    {savingFootnotes ? 'Saving...' : 'Save Footnotes'}
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>

        </div>
    );
}

// ── Styles ────────────────────────────────────────────────────────────────────
const styles = {
    // Back link
    backLink: { color: '#bb86fc', textDecoration: 'none', fontSize: '14px' },

    // 60/40 split layout
    // Shared header row above both panels
    panelHeaderRow: { display: 'grid', gridTemplateColumns: '3fr 2fr', gap: '32px', marginBottom: '0' },
    leftPaneHeader: { display: 'flex', borderBottom: '2px solid #333', gap: '2px', overflowX: 'auto' },
    rightPaneHeader: { display: 'flex', alignItems: 'center', backgroundColor: '#1e1e1e', border: '1px solid #333', borderBottom: '2px solid #bb86fc', padding: '10px 16px', borderRadius: '8px 8px 0 0' },
    // Content row
    splitLayout: { display: 'grid', gridTemplateColumns: '3fr 2fr', gap: '32px', alignItems: 'stretch', marginBottom: '48px' },
    leftPane: { minWidth: 0, display: 'flex', flexDirection: 'column' },
    rightPane: { minWidth: 0, display: 'flex', flexDirection: 'column' },

    // Info tab bar (mirrors ArtistDetail infoTabBar)
    infoTabBar: { display: 'flex', borderBottom: '2px solid #333', gap: '2px', overflowX: 'auto' }, // legacy
    infoTabBtn: { background: 'none', border: 'none', borderBottom: '2px solid transparent', marginBottom: '-2px', padding: '10px 18px', fontSize: '14px', fontWeight: '600', color: '#777', cursor: 'pointer', whiteSpace: 'nowrap' },
    infoTabBtnActive: { color: '#bb86fc', borderBottom: '2px solid #bb86fc' },
    infoTabContent: { backgroundColor: '#1a1a1a', border: '1px solid #2a2a2a', borderTop: 'none', borderRadius: '0 0 8px 8px', padding: '20px', minHeight: '220px', flex: 1, overflowY: 'auto' },

    // Info tab layout: image + table side by side
    infoLayout: { display: 'flex', gap: '20px', alignItems: 'flex-start' },
    heroImageWrap: { flexShrink: 0 },
    heroImage: { width: '200px', height: '260px', objectFit: 'cover', borderRadius: '8px', border: '1px solid #333', display: 'block' },
    noImageBox: { width: '200px', height: '260px', borderRadius: '8px', border: '1px solid #333', background: '#1e1e1e', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '8px', flexShrink: 0 },
    noImageIcon: { fontSize: '36px', opacity: 0.3 },
    noImageLabel: { fontSize: '11px', color: '#555', textTransform: 'uppercase', letterSpacing: '0.5px' },

    // Info / Capacity table
    infoTable: { flex: 1, borderCollapse: 'collapse', fontSize: '14px', width: '100%' },
    infoLabel: { padding: '10px 16px 10px 0', color: '#aaa', fontSize: '13px', textTransform: 'uppercase', letterSpacing: '0.5px', borderBottom: '1px solid #2a2a2a', whiteSpace: 'nowrap', width: '1%', verticalAlign: 'top' },
    infoValue: { padding: '10px 0', color: '#e0e0e0', fontWeight: 'bold', fontSize: '15px', borderBottom: '1px solid #2a2a2a', lineHeight: '1.4' },
    infoLink: { color: '#bb86fc', textDecoration: 'none' },
    emptyField: { color: '#555', fontWeight: 'normal' },

    // Right pane: action row
    actionRow: { display: 'flex', gap: '10px', alignItems: 'center', justifyContent: 'flex-end', marginBottom: '16px', flexWrap: 'wrap' },
    attendedBtnOff: { backgroundColor: 'transparent', color: '#aaa', border: '1px solid #555', borderRadius: '6px', padding: '7px 16px', fontSize: '14px', cursor: 'pointer', fontWeight: '500', whiteSpace: 'nowrap' },
    attendedBtnOn: { backgroundColor: '#1a3a1a', color: '#66bb6a', border: '1px solid #66bb6a', borderRadius: '6px', padding: '7px 16px', fontSize: '14px', cursor: 'pointer', fontWeight: 'bold', whiteSpace: 'nowrap' },
    performedBadge: { backgroundColor: '#1a2a3a', color: '#64b5f6', border: '1px solid #64b5f6', borderRadius: '6px', padding: '7px 16px', fontSize: '14px', fontWeight: 'bold', whiteSpace: 'nowrap' },
    workedBadge: { backgroundColor: '#2b1a0d', color: '#ff9800', border: '1px solid #7a4200', borderRadius: '6px', padding: '7px 16px', fontSize: '14px', fontWeight: 'bold', whiteSpace: 'nowrap' },
    editButton: { backgroundColor: '#2c2c2c', color: '#bb86fc', border: '1px solid #bb86fc', borderRadius: '4px', padding: '6px 16px', fontSize: '14px', textDecoration: 'none', fontWeight: 'bold', whiteSpace: 'nowrap' },

    // Lineup
    lineupHeader: { backgroundColor: '#1e1e1e', border: '1px solid #333', borderRadius: '8px 8px 0 0', padding: '12px 16px', borderBottom: '2px solid #bb86fc' },
    lineupHeaderText: { fontSize: '14px', fontWeight: '600', color: '#e0e0e0', textTransform: 'uppercase', letterSpacing: '0.5px' },
    lineupList: { display: 'flex', flexDirection: 'column', gap: '1px', flex: 1, border: '1px solid #333', borderTop: 'none', borderRadius: '0 0 8px 8px', overflow: 'hidden' },
    lineupCard: { backgroundColor: '#1e1e1e', border: '1px solid #2a2a2a', borderTop: 'none', padding: '12px 14px', display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' },
    lineupCardHeadliner: { backgroundColor: '#1c1624' },
    lineupPosition: { fontSize: '18px', color: '#bb86fc', fontWeight: 'bold', minWidth: '28px', textAlign: 'center', flexShrink: 0 },
    lineupInfo: { flex: 1, minWidth: 0 },
    artistLink: { color: '#e0e0e0', textDecoration: 'none', fontSize: '16px', fontWeight: 'bold', display: 'block', marginBottom: '2px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
    artistLocation: { fontSize: '12px', color: '#777' },
    headlinerBadge: { border: '1px solid #bb86fc', borderRadius: '4px', padding: '3px 8px', fontSize: '11px', fontWeight: 'bold', color: '#bb86fc', letterSpacing: '0.5px', whiteSpace: 'nowrap', flexShrink: 0 },
    supportBadge: { border: '1px solid #444', borderRadius: '4px', padding: '3px 8px', fontSize: '11px', fontWeight: 'bold', color: '#666', letterSpacing: '0.5px', whiteSpace: 'nowrap', flexShrink: 0 },

    // Caught buttons
    caughtBtnOff: { backgroundColor: 'transparent', color: '#888', border: '1px solid #444', borderRadius: '4px', padding: '3px 10px', fontSize: '11px', cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0 },
    caughtBtnOn: { backgroundColor: '#1a3a1a', color: '#66bb6a', border: '1px solid #66bb6a', borderRadius: '4px', padding: '3px 10px', fontSize: '11px', cursor: 'pointer', fontWeight: 'bold', whiteSpace: 'nowrap', flexShrink: 0 },
    caughtBtnMissed: { backgroundColor: '#2c1a1e', color: '#e57373', border: '1px solid #e57373', borderRadius: '4px', padding: '3px 10px', fontSize: '11px', cursor: 'pointer', fontWeight: 'bold', whiteSpace: 'nowrap', flexShrink: 0 },

    // Photos (mirrors ArtistDetail)
    photoGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: '12px' },
    photoCard: { backgroundColor: '#1e1e1e', border: '1px solid #333', borderRadius: '8px', overflow: 'hidden' },
    photoCardDefault: { borderColor: '#bb86fc' },
    photoThumb: { position: 'relative', width: '100%', paddingTop: '75%', overflow: 'hidden', cursor: 'zoom-in', background: '#111' },
    photoThumbImg: { position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', objectFit: 'cover' },
    defaultBadge: { position: 'absolute', top: '6px', left: '6px', background: '#bb86fc', color: '#121212', fontSize: '10px', fontWeight: 'bold', borderRadius: '4px', padding: '2px 6px' },
    privateBadge: { position: 'absolute', top: '6px', right: '6px', background: 'rgba(0,0,0,0.6)', borderRadius: '4px', padding: '2px 4px', fontSize: '12px' },
    photoCardBody: { padding: '8px' },
    photoCaption: { fontSize: '11px', color: '#aaa', marginBottom: '6px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
    setDefaultBtn: { width: '100%', background: 'transparent', border: '1px solid #bb86fc', borderRadius: '4px', color: '#bb86fc', fontSize: '11px', padding: '4px 6px', cursor: 'pointer' },
    isDefaultLabel: { fontSize: '11px', color: '#bb86fc', textAlign: 'center', padding: '4px 0' },
    addPhotoBtn: { background: '#bb86fc', color: '#121212', border: 'none', borderRadius: '6px', padding: '6px 14px', fontSize: '12px', fontWeight: 600, cursor: 'pointer' },
    emptyTab: { textAlign: 'center', color: '#666', padding: '20px 0', fontSize: '14px' },

    // Lightbox
    lightboxOverlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 3000 },
    lightboxCaption: { position: 'absolute', bottom: 0, left: 0, right: 0, background: 'rgba(0,0,0,0.7)', color: '#e0e0e0', padding: '8px 12px', fontSize: '13px', textAlign: 'center', borderRadius: '0 0 8px 8px' },
    lightboxClose: { position: 'absolute', top: '8px', right: '8px', background: 'rgba(0,0,0,0.6)', border: 'none', color: '#fff', fontSize: '18px', cursor: 'pointer', borderRadius: '4px', padding: '2px 8px' },

    // Bottom section
    section: { marginBottom: '48px' },
    sectionHeaderRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', paddingBottom: '8px', borderBottom: '1px solid #333' },
    sectionTitle: { fontSize: '22px', fontWeight: 'bold', color: '#e0e0e0', margin: 0 },
    editSectionButton: { backgroundColor: '#2c2c2c', color: '#bb86fc', border: '1px solid #bb86fc', borderRadius: '4px', padding: '5px 14px', fontSize: '13px', cursor: 'pointer', fontWeight: 'bold' },

    // Footnotes - read mode
    emptyText: { color: '#666', fontSize: '14px', fontStyle: 'italic', padding: '8px 0' },
    footnoteList: { display: 'flex', flexDirection: 'column', gap: '12px' },
    footnoteCard: { backgroundColor: '#1e1e1e', border: '1px solid #333', borderRadius: '8px', padding: '16px 20px', display: 'flex', gap: '16px', alignItems: 'flex-start' },
    footnoteNumber: { fontSize: '13px', color: '#666', fontWeight: 'bold', minWidth: '20px', paddingTop: '2px' },
    footnoteContent: { flex: 1 },
    footnoteDescription: { fontSize: '15px', color: '#e0e0e0', marginBottom: '8px', lineHeight: '1.5' },
    footnoteMeta: { display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'center' },
    footnoteArtistLink: { color: '#bb86fc', textDecoration: 'none', fontSize: '13px', fontWeight: 'bold', backgroundColor: '#2c2c2c', border: '1px solid #444', borderRadius: '4px', padding: '3px 10px' },
    footnoteUrlLink: { color: '#bb86fc', textDecoration: 'none', fontSize: '13px', fontWeight: 'bold' },

    // Footnotes - edit mode
    editBox: { backgroundColor: '#1a1a1a', border: '1px solid #333', borderRadius: '8px', padding: '24px' },
    footnoteErrorMsg: { backgroundColor: '#2c1a1e', border: '1px solid #cf6679', borderRadius: '4px', padding: '10px 14px', color: '#cf6679', marginBottom: '16px', fontSize: '14px' },
    footnoteEditCard: { backgroundColor: '#1e1e1e', border: '1px solid #3a3a3a', borderRadius: '8px', padding: '16px', marginBottom: '12px' },
    footnoteEditHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' },
    footnoteEditNumber: { fontSize: '13px', color: '#666', fontWeight: 'bold' },
    removeFootnoteButton: { backgroundColor: 'transparent', color: '#cf6679', border: '1px solid #cf6679', borderRadius: '4px', padding: '4px 10px', fontSize: '12px', cursor: 'pointer' },
    footnoteEditField: { marginBottom: '12px' },
    footnoteEditRow: { display: 'flex', gap: '12px' },
    footnoteEditLabel: { display: 'block', fontSize: '12px', color: '#888', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '6px' },
    footnoteInput: { backgroundColor: '#2c2c2c', border: '1px solid #444', borderRadius: '4px', padding: '8px 12px', fontSize: '14px', color: '#e0e0e0', width: '100%', boxSizing: 'border-box' },
    footnoteEditActions: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '16px' },
    addFootnoteButton: { backgroundColor: '#2c2c2c', color: '#bb86fc', border: '1px solid #bb86fc', borderRadius: '4px', padding: '7px 14px', fontSize: '13px', cursor: 'pointer', fontWeight: 'bold' },
    footnoteEditButtons: { display: 'flex', gap: '10px' },
    cancelButton: { backgroundColor: 'transparent', color: '#aaa', border: '1px solid #444', borderRadius: '4px', padding: '8px 20px', fontSize: '14px', cursor: 'pointer' },
    saveButton: { backgroundColor: '#bb86fc', color: '#121212', border: 'none', borderRadius: '4px', padding: '8px 20px', fontSize: '14px', fontWeight: 'bold', cursor: 'pointer' },

    // Generic
    error: { textAlign: 'center', color: '#cf6679', fontSize: '16px', padding: '60px 0' },
};

export default EventDetail;
